/**
 * C.15 — NIPE topic tile catalog.
 *
 * Eight tiles per the milestone spec (Q17). Operator-gated tiles are
 * marked with `operatorOnly: true`; the tile grid filters them via
 * `useRepancyRole`.
 *
 * Tile copy MUST stay client-agnostic per `feedback_skills_no_client_data`.
 * The bodies below are short walkthrough markdown — full topic
 * walkthroughs live in `./bodies/*.md` (rendered when the tile is
 * clicked).
 */

export interface NipeTopic {
  key: string;
  label: string;
  icon: string;
  /** When set, renders an <img> instead of the emoji character. */
  iconSrc?: string;
  blurb: string;
  body: string;
  operatorOnly?: boolean;
  /**
   * Top-tier tiles render in the lawyer-first NipeChatPanel empty
   * state. Non-top-tier topics stay in the catalog so direct API
   * callers / future surfaces can still look them up, but they don't
   * show up in the tile grid.
   */
  topTier?: boolean;
  /**
   * Optional richer user-prompt sent to the model when the tile is
   * clicked. Falls back to the topic body when absent. Avoids the
   * "Tell me more about <label>." title-only prompt that gave the
   * model nothing to work with.
   */
  initialPrompt?: string;
  /**
   * Action tile — when set, clicking the tile dispatches a local
   * window-open instead of starting a chat. The action runs
   * synchronously via WindowContext.openWindow; no LLM round-trip,
   * no token cost. Pairs with `actionWindowPath`.
   */
  action?: 'open-window';
  /** Path passed to WindowContext.openWindow when action='open-window'. */
  actionWindowPath?: string;
}

export const NIPE_TOPICS: NipeTopic[] = [
  {
    key: 'what-is-repancy',
    label: 'What is Repancy?',
    icon: '👋',
    blurb: 'A 30-second tour.',
    body: `Repancy is a legal AI workbench that runs inside your own SharePoint tenant.

- **Real SharePoint**: Document Library, vault notes, and saves all hit your tenant via Microsoft Graph. Nothing lives in a Repancy database.
- **Skills**: reusable prompts (SKILL.md files) you keep in your vault. Combine them in Prompt Lab against a document.
- **Specialization**: a thin LoRA adapter trained on your firm's Skills. Open the Specialization Lab to see base-vs-specialized output side by side.
- **NIPE** (this chat): can answer questions, run tools (open windows, propose SharePoint writes via an approval tray), and walk you through features.
- **Demo mode**: anonymous preview wired against a sandbox tenant — try anything safely.`,
    initialPrompt: `In two short paragraphs, tell me what Repancy is and what I should try first. I'm a lawyer who just signed in for the first time. Mention SharePoint integration, Skills, and the Specialization Lab. Keep it concrete — no marketing language.`,
  },
  {
    key: 'vault',
    label: 'Obsidian Vault',
    icon: '🟣',
    blurb: 'Tree, editor, graph, search.',
    body: `Your vault holds notes, cases, doctrine, and skills as plain markdown.

- Open with the Obsidian Vault icon on the desktop or via the Start menu.
- Three-pane layout: tree on the left, editor in the middle, graph on the right.
- Click any wikilink to open it in the second pane (split view).
- Press Cmd+P for the search palette.
- Toggle Edit / Read-only with the button in the top-right; edits autosave through the save provider.`,
  },
  {
    key: 'sharepoint',
    label: 'SharePoint',
    icon: '📁',
    blurb: 'Document library + portal sync.',
    body: `Repancy talks to SharePoint Online via Microsoft Graph for the firm's document library.

- The Document Library window mirrors the firm site you have access to.
- Vault notes import from a SharePoint folder via Obsidian Import; round-trip save lands edits back as Word/Markdown files.
- Conflicts surface via the ConflictDialog when the cloud copy has moved on.`,
  },
  {
    key: 'skills',
    label: 'Skills + Prompt Lab',
    icon: '📜',
    iconSrc: '/xp/icons/icon__notepad--large.png',
    blurb: 'Reusable prompts; SKILL.md.',
    topTier: true,
    body: `Skills are reusable prompts stored as SKILL.md files in your vault under Skills/.

- Open the Skill Library to browse what is registered.
- Open the Prompt Lab to compose a multi-skill prompt against a document.
- Ask me to listSkills, getSkill, or applySkill against the current note.`,
    initialPrompt: `Explain how Skills and the Prompt Lab fit together. I'm a lawyer evaluating Repancy. Cover: what a SKILL.md file is, how I'd write one for a recurring task (e.g. NDA redline), and how Prompt Lab composes multiple skills against a document. Use 3-5 short paragraphs.`,
  },
  {
    key: 'lora',
    label: 'LoRAs & Skill Specializations',
    icon: '🧠',
    iconSrc: '/xp/icons/icon__computer--large.png',
    blurb: 'Train your firm into the model.',
    topTier: true,
    initialPrompt: `Explain LoRAs and Skill Specializations in Repancy in plain English, for a lawyer who has never trained a model. Cover: (1) what a LoRA is — a thin, additive AND subtractive layer on top of the base model, NOT new legal knowledge, takeable off at any time; (2) what a Skill Specialization is — a LoRA trained on one or more of the firm's SKILL.md files so the model writes in that house style; (3) the difference between a single-skill specialization and a bundled-skills specialization; (4) where to see it work — the Specialization Lab runs base-vs-specialized side by side and the strength slider scrubs between them. Use 3-4 short paragraphs.`,
    body: `A specialization is a small adjustment to the base model that focuses it on certain patterns and de-emphasizes others. We build one from the Skills you pick.

Specialization is additive AND subtractive:
- Additive: amplifies words, structures, and citation styles that show up across the Skills you trained on.
- Subtractive: quiets the moves that show up less in your Skills than in the base model's average behavior.

It is NOT new legal knowledge. It is NOT memorization. It is a thin overlay (a few MB) layered on top of the base model — you can take it off any time.

To see what it does: open the Specialization Lab. It runs the same prompt twice — once on the base model, once with your specialization layered on — and shows the two outputs side by side. The LoRA-strength slider scrubs between pure-base (0%) and full-specialization (100%).

In operator-facing parts of the app this is called a LoRA (Low-Rank Adaptation). Same thing, technical name.`,
  },
  {
    key: 'corpus-search',
    label: 'Corpus Search',
    icon: '🔎',
    blurb: 'Vector search over the firm corpus.',
    body: `Corpus Search runs vector + keyword retrieval over every document the firm portal has indexed.

- Open the Corpus Search window from the desktop.
- Filter by client, matter, or document type.
- Click a result to open the source document in the Document Library.`,
  },
  {
    key: 'portal-provisioning',
    label: 'Portal Provisioning',
    icon: '🆕',
    blurb: 'Stand up a new firm portal.',
    // J.3 — operatorOnly remains for the operator-flavored wizard
    // walkthrough, but the rationale copy ("You see this tile because
    // your role is repancy_operator") is gone — every signed-in user
    // can create their own portal now via the J.3 panel + the wizard.
    operatorOnly: true,
    body: `Provision a new firm portal through the New Portal wizard.

- Step 1: pick a template.
- Step 2: upload reference documents.
- Step 3: sanitize PII via the three-layer pipeline.
- Step 4: provision Azure Search + SharePoint structure via the Function App.`,
  },
  {
    key: 'sanitize',
    label: 'Sanitize Pipeline',
    icon: '🧼',
    blurb: 'PII redaction for new portals.',
    operatorOnly: true,
    body: `The sanitize pipeline replaces PII in template documents before they seed a new portal.

- Layer 1: deterministic regex pass (SSN, EIN, phone, email, Luhn-checked cards).
- Layer 2: Claude via OpenRouter — contextual replacements for proper names and matter numbers.
- Layer 3: diff + replacement map surfaced in wizard step 3 for operator approval.`,
  },
  {
    // J.3 — opt-in "Create my private workspace" entry. One of the 4
    // entry points (Start Menu + Desktop icon + Settings tab + NIPE
    // menu). NipeTopicTileGrid clicks fire the topic's onSelect
    // callback; the NipeChatPanel reads the body. To open the actual
    // workspace setup window, the chat panel listener calls
    // openWindow('/admin/workspace-setup').
    key: 'create-private-workspace',
    label: 'I want my own workspace',
    icon: '🆕',
    blurb: 'Mint a private workspace in 60 seconds.',
    body: `Every signed-in user can create their own private workspace — a personal Repancy portal with its own SharePoint site, Skills library, and demo content.

The shared morrison-legal portal is read-only. Your private workspace is fully editable.

How to mint one:
- Click the "Create my private workspace" Start Menu item, OR
- Double-click the matching desktop icon, OR
- Open Settings → Workspace tab.

All three open the same WorkspaceSetupPanel where you can pick a custom slug and kick off provisioning. The whole flow takes about a minute; you can minimize the window and keep working while it provisions.`,
  },
  {
    key: 'matter-profile',
    label: 'Matter Intake & Profile',
    icon: '🗂️',
    blurb: 'Per-matter profile + intake.',
    body: `Matter Intake & Profile is the per-matter container for client facts, parties, evidence, and timeline.

- Open from the Client List by drilling into a client → matter.
- The profile holds context that flows into every NIPE prompt as runtime metadata.
- Use the .flow editor to map case structure (parties, evidence, citations).`,
  },
  {
    key: 'demo-trust',
    label: 'Demo & Trust',
    icon: '🔒',
    blurb: 'How demo mode works.',
    initialPrompt: `Explain how Repancy's demo mode works and why it's safe to try. I'm a lawyer worried about data leaks. Be specific: demo data lives in dedicated demo* Convex tables paired with the live tables, demo and live are never mixed, demo NIPE chats route through the firm key (so I don't need an API key), and the demo SharePoint is a sandbox tenant. End with one concrete thing I should try first.`,
    body: `Demo mode is the anonymous-no-account preview of Repancy.

- Demo data lives in demo* tables in Convex, paired with the live tables.
- Demo NIPE chats route through the firm key (Convex backup) and are not billed to a user.
- Flip showDemo in tenantBranding to expose the demo desktop on a portal.
- Live tables and demo tables are never mixed.`,
  },
  // Action tiles — clicking dispatches a local WindowContext.openWindow
  // call. No LLM round-trip; the user can come back to chat afterwards.
  {
    key: 'open-specialization-lab',
    label: 'Show me the Specialization Lab',
    icon: '🧪',
    iconSrc: '/xp/icons/icon__search--large.png',
    blurb: 'Open the side-by-side base vs LoRA comparison.',
    topTier: true,
    action: 'open-window',
    actionWindowPath: '/specialization-lab',
    body: `Opens the Specialization Lab window — the side-by-side panel that runs the same prompt against the base model and your firm's specialization, with a strength slider to scrub between them.`,
  },
  {
    key: 'open-onboard-wizard',
    label: 'Set up my own SharePoint',
    icon: '🆕',
    iconSrc: '/xp/icons/icon__network_places--large.png',
    blurb: 'Open the portal-provisioning wizard.',
    topTier: true,
    action: 'open-window',
    actionWindowPath: '/onboard/new',
    body: `Opens the customer onboarding wizard — discovers your M365 tenant, walks through admin consent, signs you in, then provisions a real SharePoint site under your firm name.`,
  },
];
